/**
 * 共通モーダル。
 *
 * ブラウザ標準の <dialog> + showModal() で表示する(2026-08-24 改修)。
 * - dialog はブラウザの「トップレイヤー」に描画されるため、ページ側のCSS
 *   (レイアウト・transform・overflow・z-index)の影響を一切受けず、
 *   常に画面の最前面・中央に表示される。従来のフレックス中央寄せ方式で
 *   一部環境において表示が崩れる報告があったための構造的な対策
 * - createPortal で body 直下に置き、表示中は背面のスクロールを止める
 * - Esc キー(cancelイベント)と背景(backdrop)クリックで閉じる
 * - 万一 showModal 非対応の環境でも、open 属性 + fixed 中央寄せで表示される
 */
import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export default function Modal({
  onClose,
  width = 'min(980px, 94vw)',
  children,
  showClose = true,
  fullscreen = false,
  compact = false,
}: {
  onClose: () => void;
  width?: string;
  children: ReactNode;
  /** 右上固定の「閉じる」を出すか。確認ダイアログ等、独自ボタンだけで閉じる場合は false */
  showClose?: boolean;
  /** 画面いっぱいに表示する(添付プレビューをスマホで大きく見せる。2026-08-31) */
  fullscreen?: boolean;
  /** 中身の高さに合わせた小さな箱にする(短い確認用。2026-09-17)。既定は画面の高さいっぱいまで伸びる */
  compact?: boolean;
}) {
  const ref = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      (dialog as HTMLElement).setAttribute('open', '');
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  /** backdrop(dialogの外側)をクリックしたときだけ閉じる。内側の余白クリックでは閉じない */
  function onBackdropClick(e: MouseEvent<HTMLDialogElement>) {
    const dialog = ref.current;
    if (!dialog || e.target !== dialog) return;
    const r = dialog.getBoundingClientRect();
    const outside =
      e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
    if (outside) onClose();
  }

  return createPortal(
    <dialog
      ref={ref}
      // .modal のような汎用クラス名は使わない: ブラウザ拡張機能が汎用名へ
      // !important スタイルを注入して表示が壊れた実例があるため。
      // 必須ジオメトリは layout.css の dialog.sc-dialog(!important)でも自衛している
      className={`sc-dialog${fullscreen ? ' sc-dialog-full' : ''}${compact ? ' sc-dialog-compact' : ''}`}
      onCancel={(e) => {
        // Escキー。dialog自身が閉じるのではなく、親のstate経由でアンマウントさせる
        e.preventDefault();
        onClose();
      }}
      onClick={onBackdropClick}
      style={{
        // showModal が使えない環境へのフォールバックも兼ねて位置は明示する
        // (トップレイヤー表示時もこの指定は中央寄せとして有効)
        ['--sc-dialog-width' as never]: width,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        margin: 'auto',
        width: fullscreen ? '100vw' : width,
        maxWidth: fullscreen ? '100vw' : 'calc(100vw - 32px)',
        height: fullscreen ? '100dvh' : undefined,
        maxHeight: fullscreen ? '100dvh' : 'calc(100vh - 40px)',
        // 中身は通常フローで積む(display:flexにしない)。
        // 高さが未確定の親の中で flex:1 の子は高さ0に計算されることがあり
        // (Chrome 151で顕在化)、「中身が潰れて帯状になる」原因になるため。
        // 入りきらないときはモーダル自体がスクロールする
        display: 'block',
        overflowY: 'auto',
        background: '#fff',
        color: 'inherit',
        border: 'none',
        borderRadius: fullscreen ? 0 : 12,
        // 全画面時は余白を詰めて中身(画像・PDF)を最大限見せる
        padding: fullscreen ? '8px 8px calc(8px + env(safe-area-inset-bottom))' : '16px 18px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, .35)',
        zIndex: 1000,
      }}
    >
      {/* 「閉じる」は右上に固定(2026-08-28 指示): スクロールしても常に見える。
          白背景の帯にすることで、下を通る本文と重ならない */}
      {showClose && (
        <div
          style={{
            position: 'sticky',
            top: fullscreen ? -8 : -16,
            zIndex: 30,
            display: 'flex',
            justifyContent: 'flex-end',
            background: '#fff',
            margin: fullscreen ? '-8px -8px 4px' : '-16px -18px 6px',
            // 全画面(添付・PDFの表示)は画面の最上部に来るため、iPhoneのノッチ等の安全領域の分を空ける(2026-09-16 改修依頼 F)
            padding: fullscreen ? 'calc(6px + env(safe-area-inset-top)) 8px 4px' : '10px 14px 6px',
            borderRadius: fullscreen ? 0 : '12px 12px 0 0',
          }}
        >
          <button
            className="sc-dialog-close"
            style={
              fullscreen
                ? // 添付・PDFの表示画面の「閉じる」(2026-09-16 改修依頼 F): 従来の約1.8倍の大きさにし、
                  // 右端から少し内側へ寄せる。指で押せるよう最低44px四方を確保する
                  {
                    float: 'none',
                    fontSize: 23,
                    padding: '7px 22px',
                    borderRadius: 10,
                    minWidth: 44,
                    minHeight: 44,
                    marginRight: 'clamp(12px, 6vw, 72px)',
                    whiteSpace: 'nowrap',
                  }
                : { float: 'none' }
            }
            onClick={onClose}
          >
            閉じる
          </button>
        </div>
      )}
      {children}
    </dialog>,
    document.body,
  );
}
